import * as React from 'react';
import { cn } from '@/lib/cn';

interface ProviderLogoProps {
  /**
   * Provider slug — same identifier as `providerId` on the MCP catalog and
   * `provider` on integration adapters. Resolves to
   * `/logos/<slug>-{icon|wordmark}.svg`. When omitted (or the asset 404s),
   * a 2-letter monogram derived from `displayName` is rendered instead.
   */
  slug?: string;
  /** Used for the alt text and the fallback monogram. */
  displayName?: string;
  /**
   * `icon` (default): square mark. Sizing controlled by `className`
   * (defaults to `h-10 w-10`).
   * `wordmark`: rectangular. Sizing controlled by `className`
   * (defaults to `h-6 w-auto`).
   */
  variant?: 'icon' | 'wordmark';
  className?: string;
  /** Extra classes applied to the monogram fallback (icon variant only). */
  fallbackClassName?: string;
  /**
   * Render the mark as a monochrome silhouette so a grid of brands reads as
   * one visual texture (no "color fruit salad"). Light mode → solid black,
   * dark mode → solid white. Defaults to false — detail surfaces show the
   * mark in full color; list / picker surfaces opt in.
   */
  grayscale?: boolean;
}

function initials(name?: string): string {
  if (!name) return '??';
  const stripped = name.replace(/[^A-Za-z0-9]/g, '');
  return (stripped || '??').slice(0, 2).toUpperCase();
}

/**
 * Renders a provider logo (icon or wordmark) backed by a static asset under
 * `public/logos/`. See `public/logos/README.md` for the file convention and
 * how to add new brand assets.
 *
 * The icon variant probes a candidate cascade (full slug → brand prefix) and
 * falls back to a 2-letter monogram when no asset loads. The wordmark variant
 * falls back to the brand icon when there's no `<slug>-wordmark.svg` (rather
 * than inventing a text wordmark), and to plain `displayName` text only when
 * there's no slug at all.
 */
export function ProviderLogo({
  slug,
  displayName,
  variant = 'icon',
  className,
  fallbackClassName,
  grayscale = false,
}: ProviderLogoProps) {
  if (variant === 'wordmark') {
    return (
      <WordmarkLogo
        slug={slug}
        displayName={displayName}
        className={className}
        grayscale={grayscale}
      />
    );
  }

  return (
    <IconLogo
      slug={slug}
      displayName={displayName}
      className={cn('h-10 w-10 rounded-md', className)}
      fallbackClassName={fallbackClassName}
      grayscale={grayscale}
    />
  );
}

/**
 * Candidate icon slugs, most specific first: the full slug, then the brand
 * prefix (the segment before the first `-`). This lets a backend
 * `{brand}-{product}` id fall back to the brand mark when no product-specific
 * asset exists — `google-gmail` → `google`, `optr-kb` → `optr` — before the
 * monogram. A single-segment slug (`slack`, `aws`) has no prefix to try.
 */
function iconCandidates(slug: string): string[] {
  const out = [slug];
  const dash = slug.indexOf('-');
  if (dash > 0) {
    const brand = slug.slice(0, dash);
    if (brand && brand !== slug) out.push(brand);
  }
  return out;
}

interface IconLogoProps {
  slug?: string;
  displayName?: string;
  className?: string;
  fallbackClassName?: string;
  grayscale?: boolean;
}

/**
 * Track whether the app is in dark mode (the `.dark` class on <html>, set by
 * `applyTheme`). Re-renders when the theme toggles so logos can swap to a
 * dark-mode asset variant live.
 */
function useIsDarkMode(): boolean {
  const read = () =>
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');
  const [isDark, setIsDark] = React.useState(read);
  React.useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setIsDark(el.classList.contains('dark')));
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    setIsDark(el.classList.contains('dark'));
    return () => obs.disconnect();
  }, []);
  return isDark;
}

/**
 * Ordered asset URLs to try for an icon. For each candidate slug (full →
 * brand prefix) we prefer a `-icon-dark.svg` variant in dark mode + full color
 * — some marks are dark-on-transparent (X, GitHub, Operator, the AWS wordmark)
 * and vanish on dark surfaces. Grayscale always uses the base asset since it's
 * recolored to the foreground via a CSS mask. Each variant falls back to the
 * base asset when absent, so dark variants are purely opt-in per brand.
 */
function iconAssetUrls(slug: string, preferDark: boolean): string[] {
  const urls: string[] = [];
  for (const s of iconCandidates(slug)) {
    if (preferDark) urls.push(`/logos/${s}-icon-dark.svg`);
    urls.push(`/logos/${s}-icon.svg`);
  }
  return urls;
}

/**
 * Square brand icon. Probes the candidate assets (dark variant → base, full
 * slug → brand prefix) in order and settles on the first that loads; if none
 * do, renders the 2-letter monogram. In grayscale mode the asset is used as a
 * CSS mask so the mark adopts `text-foreground` (theme-aware) instead of being
 * filter-hacked to mono.
 */
function IconLogo({ slug, displayName, className, fallbackClassName, grayscale }: IconLogoProps) {
  const isDark = useIsDarkMode();
  const candidates = React.useMemo(
    () => (slug ? iconAssetUrls(slug, !grayscale && isDark) : []),
    [slug, grayscale, isDark],
  );
  const [idx, setIdx] = React.useState(0);
  const [status, setStatus] = React.useState<'loading' | 'ok' | 'missing'>(
    candidates.length ? 'loading' : 'missing',
  );

  React.useEffect(() => {
    setIdx(0);
    setStatus(candidates.length ? 'loading' : 'missing');
  }, [candidates]);

  React.useEffect(() => {
    if (!candidates.length || status !== 'loading') return;
    const img = new Image();
    img.onload = () => setStatus('ok');
    img.onerror = () => {
      // Asset 404'd — try the next (less specific) candidate, else give up.
      if (idx + 1 < candidates.length) setIdx(idx + 1);
      else setStatus('missing');
    };
    img.src = candidates[idx];
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [candidates, idx, status]);

  const src = status === 'ok' ? candidates[idx] : null;

  if (!src) {
    return (
      <span
        role="img"
        aria-label={displayName ?? ''}
        className={cn(
          'inline-flex items-center justify-center bg-muted text-muted-foreground text-xs font-semibold rounded-md',
          className,
          fallbackClassName,
        )}
      >
        {initials(displayName)}
      </span>
    );
  }

  if (grayscale) {
    return (
      <span
        role="img"
        aria-label={displayName ?? ''}
        className={cn('inline-block bg-foreground/90', className)}
        style={{
          maskImage: `url(${src})`,
          WebkitMaskImage: `url(${src})`,
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
        }}
      />
    );
  }

  return (
    <img src={src} alt={displayName ?? ''} className={cn('object-contain', className)} />
  );
}

interface WordmarkLogoProps {
  slug?: string;
  displayName?: string;
  className?: string;
  grayscale?: boolean;
}

function WordmarkLogo({
  slug,
  displayName,
  className,
  grayscale,
}: WordmarkLogoProps) {
  const isDark = useIsDarkMode();
  const [failed, setFailed] = React.useState(false);
  // True once a dark-variant probe 404s and we fall back to the base wordmark.
  const [useBase, setUseBase] = React.useState(false);
  React.useEffect(() => {
    setFailed(false);
    setUseBase(false);
  }, [slug, isDark, grayscale]);

  // No slug at all — nothing to resolve, so render the display name as text.
  if (!slug) {
    return (
      <span
        className={cn(
          'inline-flex items-center font-semibold text-foreground',
          className,
        )}
      >
        {displayName ?? ''}
      </span>
    );
  }

  // Slug present but no `<slug>-wordmark.svg` — fall back to the brand icon
  // (which runs its own slug → brand-prefix cascade) rather than inventing a
  // text wordmark. Sized square at the wordmark row height.
  if (failed) {
    return (
      <IconLogo
        slug={slug}
        displayName={displayName}
        className="h-6 w-6 rounded-md"
        grayscale={grayscale}
      />
    );
  }

  const baseSrc = `/logos/${slug}-wordmark.svg`;

  if (grayscale) {
    // Use a CSS mask so the wordmark adopts the foreground color — same
    // theme-aware behaviour as the icon variant (base asset; the mask recolors
    // it). Probe with a hidden `<img>` so a 404 falls back to the icon above.
    return (
      <>
        <img src={baseSrc} alt="" className="hidden" onError={() => setFailed(true)} />
        <span
          role="img"
          aria-label={displayName ?? ''}
          className={cn('inline-block bg-foreground/90 h-6 w-32', className)}
          style={{
            maskImage: `url(${baseSrc})`,
            WebkitMaskImage: `url(${baseSrc})`,
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskPosition: 'left center',
            WebkitMaskPosition: 'left center',
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
          }}
        />
      </>
    );
  }

  // Full color: on dark surfaces prefer a `<slug>-wordmark-dark.svg` variant
  // (base wordmarks are often dark text that vanishes on dark), falling back to
  // the base wordmark, then to the brand icon.
  const src = isDark && !useBase ? `/logos/${slug}-wordmark-dark.svg` : baseSrc;
  return (
    <img
      src={src}
      alt={displayName ?? ''}
      className={cn('h-6 w-auto object-contain', className)}
      onError={() => {
        if (isDark && !useBase) setUseBase(true);
        else setFailed(true);
      }}
    />
  );
}
